/**
 * What became of a reconstruction, said without overclaiming.
 *
 * Promotion enqueues a reconstruction automatically, so the operator does
 * nothing and something runs. Until the request route existed there was no way
 * to say what: a promoted detection either produced records eventually or did
 * not, and the product could not tell those apart, let alone say why.
 *
 * Three claims are easy to make here and all three would be false:
 *
 * - that a dispatched execution is a finished reconstruction,
 * - that a REFUSED request has a run to look at,
 * - that records the pass wrote are records a reader can see.
 *
 * The contract is built so the second cannot be made by accident -
 * `reconstructionRunId` is null on REFUSED - and this file makes the other two
 * as hard.
 */

export type RequestState =
  | 'REQUESTED'
  | 'DISPATCHED'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'REFUSED'
  | 'DISPATCH_FAILED';

export interface Request {
  reconstructionRequestId: string;
  toolSlug: string;
  state: string;
  reconstructionRunId?: string | null;
  failureCode?: string | null;
  recordedEntities?: number | null;
  publishedEntities?: number | null;
  rejectedRecords?: number | null;
  exportsFound?: number | null;
  exportsRead?: number | null;
  attempts: number;
  requestedAt: string;
  completedAt?: string | null;
}

export interface Reading {
  /** The word for this state, in the operator's language. */
  label: string;
  /** ok while it is working or worked, warn where something needs a person. */
  tone: 'ok' | 'mut' | 'warn' | 'bad';
  /** Whether a run exists to look at. Never true without an id. */
  hasRun: boolean;
  /** Whether asking again could plausibly change the outcome. */
  retryable: boolean;
  /** One sentence: what this state means, not what it is called. */
  means: string;
}

const STATE: Record<RequestState, Omit<Reading, 'hasRun'>> = {
  REQUESTED: {
    label: 'Queued',
    tone: 'mut',
    retryable: false,
    means: 'Asked for, and no executor has claimed it yet.',
  },
  DISPATCHED: {
    label: 'Running',
    tone: 'mut',
    retryable: false,
    means: 'An executor holds this under a lease and is reading the export now.',
  },
  SUCCEEDED: {
    label: 'Finished',
    tone: 'ok',
    retryable: false,
    means: 'The pass completed and read every record set it found.',
  },
  FAILED: {
    label: 'Failed',
    tone: 'bad',
    retryable: true,
    means: 'A run was opened and did not finish. The run itself records what happened.',
  },
  REFUSED: {
    label: 'Refused',
    tone: 'warn',
    retryable: false,
    means:
      'No run was ever opened, so there is nothing to inspect. The export could not be read, the scope has no content-read grant, or the detection is not confirmed.',
  },
  DISPATCH_FAILED: {
    label: 'Never started',
    tone: 'warn',
    retryable: true,
    means: 'The execution was not accepted. The asking is recorded; nothing ran.',
  },
};

const UNKNOWN: Omit<Reading, 'hasRun'> = {
  label: 'State not recognised here',
  tone: 'warn',
  retryable: false,
  means: 'This build does not recognise the state this request reported.',
};

export function read(request: Request): Reading {
  const known = STATE[request.state as RequestState] ?? UNKNOWN;
  return {
    ...known,
    /**
     * A run exists only when the request names one.
     *
     * Keying this on the state alone would offer a link on a FAILED request
     * whose id had not been written yet, and the whole point of the
     * distinction is that a link must never be offered for a run that was
     * never opened.
     */
    hasRun: typeof request.reconstructionRunId === 'string' && request.reconstructionRunId.length > 0,
  };
}

/**
 * The counts worth showing, and only where they are counts.
 *
 * `recordedEntities` and `publishedEntities` are different claims: the first is
 * what the pass wrote, the second what a reader can actually see. Showing one
 * as the other would make a pass that recorded fifty and published none read as
 * a success with fifty records behind it. A null is not a zero - a pass that
 * has not reported a number has not counted one - so nothing is invented for a
 * request still running.
 */
export function counts(request: Request): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  const n = (v: number | null | undefined) => (typeof v === 'number' ? v.toLocaleString() : null);

  const published = n(request.publishedEntities);
  const recorded = n(request.recordedEntities);
  if (published !== null) out.push({ label: 'readable records', value: published });
  if (recorded !== null && recorded !== published) out.push({ label: 'records written', value: recorded });
  const rejected = n(request.rejectedRecords);
  if (rejected !== null) out.push({ label: 'entries it could not reconstruct', value: rejected });

  // Partial reads are the fact the counts exist to expose; the pair is only
  // worth showing when it says something, which is when they differ.
  const found = request.exportsFound;
  const read_ = request.exportsRead;
  if (typeof found === 'number' && typeof read_ === 'number' && found !== read_) {
    out.push({ label: 'record sets read', value: `${read_.toLocaleString()} of ${found.toLocaleString()}` });
  }
  if (request.attempts > 1) out.push({ label: 'attempts', value: String(request.attempts) });
  return out;
}
